"use client";

import React from "react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, ExternalLink, Link } from "lucide-react";
import type { TimelineItem as TimelineItemType } from "../hooks/timeline-types";
import { SafeEmailBody } from "@/components/crm/messages/components/SafeEmailBody";
import { TimelineEmailBody } from "./TimelineEmailBody";
import { TimelineItemMeta } from "./TimelineItemMeta";
import { EmailOpenBadge } from "./EmailOpenBadge";
import { getStatusColor } from "../utils/timeline-item-utils";
import { getChannelVisual, getDirectionMeta } from "../utils/channel-visuals";

interface TimelineItemProps {
  item: TimelineItemType;
  compact?: boolean;
  className?: string;
  onClick?: (item: TimelineItemType) => void;
  onCopyLink?: (item: TimelineItemType) => void;
  /** How many timeline rows share this Gmail thread identifier on the current page */
  threadEntriesOnPage?: number;
  /** Fetch the S3-offloaded email body immediately (used for the top rows). */
  eagerBody?: boolean;
}

function formatTime(dateString: string): string {
  try {
    return format(new Date(dateString), "h:mm a");
  } catch {
    return dateString;
  }
}

function formatFull(dateString: string): string {
  try {
    return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
  } catch {
    return dateString;
  }
}

const TimelineItemComponentBase = ({
  item,
  compact = false,
  className = "",
  onClick,
  onCopyLink,
  threadEntriesOnPage,
  eagerBody = false,
}: TimelineItemProps) => {
  const { label: channelLabel } = getChannelVisual(item);
  const direction = getDirectionMeta(item.direction);
  const statusColorClass = getStatusColor(item.status);
  const isThread = typeof threadEntriesOnPage === "number" && threadEntriesOnPage > 1;

  const titleSize = compact ? "text-sm" : "text-sm sm:text-base";

  return (
    <div
      className={`rounded-lg border bg-card ${compact ? "p-2.5" : "p-3"} transition-shadow hover:shadow-sm ${onClick ? "cursor-pointer" : ""} ${className}`}
      onClick={() => onClick?.(item)}
    >
      {/* Header: title + subline on the left, timestamp + actions on the right */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className={`truncate font-medium text-card-foreground ${titleSize}`}>
              {item.title}
            </h3>
          </div>

          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span className="font-medium text-foreground/70">{channelLabel}</span>
            {direction && (
              <span className={`inline-flex items-center gap-0.5 ${direction.className}`}>
                <direction.Icon className="h-3 w-3" />
                {direction.label}
              </span>
            )}
            {isThread && (
              <Badge
                variant="outline"
                className="px-1 py-0 text-[10px] font-normal capitalize"
              >
                Thread ({threadEntriesOnPage})
              </Badge>
            )}
            {item.status && (
              <Badge
                variant="secondary"
                className={`px-1.5 py-0 text-[10px] capitalize ${statusColorClass}`}
              >
                {item.status}
              </Badge>
            )}
            {item.open_tracking && <EmailOpenBadge openTracking={item.open_tracking} />}
            {/* Inherited company-line activity — a front-desk call or info@
                mail owned by the company, shown here rather than copied onto
                this person's record. Marked so it is never mistaken for their
                own conversation. */}
            {item.scope === "company" && (
              <Badge
                variant="outline"
                className="gap-1 px-1.5 py-0 text-[10px] font-normal text-muted-foreground"
                title={
                  item.inherited_from?.methodLabel
                    ? `Company line ${item.inherited_from.methodLabel}${
                        item.inherited_from.companyName
                          ? ` · ${item.inherited_from.companyName}`
                          : ""
                      }`
                    : "Shared company line or mailbox"
                }
              >
                <Building2 className="h-3 w-3" />
                Company line
              </Badge>
            )}
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-1">
          <time
            className="whitespace-nowrap text-xs text-muted-foreground"
            title={formatFull(item.occurred_at)}
            dateTime={item.occurred_at}
          >
            {formatTime(item.occurred_at)}
          </time>
          {onCopyLink && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={(e) => {
                e.stopPropagation();
                onCopyLink(item);
              }}
              title="Copy link to this item"
            >
              <Link className="h-3.5 w-3.5" />
            </Button>
          )}
          {item.file_url && (
            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" asChild>
              <a
                href={item.file_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
          )}
        </div>
      </div>

      {/* Body. S3-offloaded emails auto-load the full body (COMMS_BODY_S3_AND_FTS);
          inline bodies (calls, notes, sms) render directly from the row. */}
      {item.type === "communication" && item.body_s3_key ? (
        <div className="mt-1.5 text-sm text-card-foreground">
          <TimelineEmailBody communicationId={item.id} eager={eagerBody} />
        </div>
      ) : item.body?.trim() ? (
        <div className="mt-1.5 text-sm text-card-foreground">
          <SafeEmailBody
            body={item.body}
            variant={compact ? "snippet" : "full"}
            stopLinkClickPropagation={Boolean(onClick)}
            collapseQuotedReplies
          />
        </div>
      ) : null}

      {!compact && <TimelineItemMeta item={item} />}
    </div>
  );
};

export const TimelineItemComponent = React.memo(TimelineItemComponentBase);
